import { useEffect, useMemo, useState } from "react";
import { applyEffect } from "../lib/voiceEffects";
import { VoiceEffectPicker } from "../components/VoiceEffectPicker";
import { MoodTagEditor } from "../components/MoodTagEditor";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Mic, Pause, Play, Square, RotateCcw, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatDuration } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useRecorder } from "../hooks/useRecorder";
import { Waveform } from "../components/Waveform";

const RoundBtn = ({ testid, onClick, children, primary, danger }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`w-14 h-14 rounded-full grid place-items-center transition-transform duration-200 hover:scale-105 active:scale-95 ${
      primary ? "btn-gold w-20 h-20" : danger ? "bg-red-500/90 text-white shadow-[0_0_30px_rgba(239,68,68,0.4)]" : "border border-[#E5C158]/30 text-[#E5C158] hover:bg-[#E5C158]/10"
    }`}
  >
    {children}
  </button>
);

export default function Record() {
  const { t, lang: uiLang } = useI18n();
  const nav = useNavigate();
  const r = useRecorder();
  const [lang, setLang] = useState(uiLang);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const { user, mergeUser } = useAuth();
  const favorite = user?.default_voice_effect || "normal";
  const [effect, setEffect] = useState(favorite);
  const [mood, setMood] = useState(null);
  const [tags, setTags] = useState([]);
  const [fxBlob, setFxBlob] = useState(null);
  const [processing, setProcessing] = useState(false);
  const previewUrl = useMemo(() => (fxBlob ? URL.createObjectURL(fxBlob) : null), [fxBlob]);

  useEffect(() => {
    if (!r.blob) { setFxBlob(null); setEffect(favorite); return; }
    let cancelled = false;
    setProcessing(true);
    applyEffect(r.blob, effect)
      .then((b) => !cancelled && setFxBlob(b))
      .catch(() => { if (!cancelled) { toast.error(t("fx_error")); setEffect("normal"); } })
      .finally(() => !cancelled && setProcessing(false));
    return () => { cancelled = true; };
  }, [r.blob, effect]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    try { await r.start(); } catch { toast.error(t("rec_mic_error")); }
  };
  const setFavorite = async (fx) => {
    try {
      const { data } = await api.patch("/auth/preferences", { default_voice_effect: fx });
      mergeUser(data);
      toast.success(t("fx_fav_saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const save = async () => {
    setSaving(true);
    const fd = new FormData();
    fd.append("audio", r.blob, `entry.${r.blob.type.includes("mp4") ? "m4a" : "webm"}`);
    if (effect !== "normal") fd.append("stored_audio", fxBlob, "entry.wav");
    fd.append("voice_effect", effect);
    fd.append("mood", mood || "");
    fd.append("tags", JSON.stringify(tags));
    fd.append("language", lang);
    fd.append("title", title);
    fd.append("duration", String(r.elapsed));
    try {
      const { data } = await api.post("/entries", fd);
      toast.success(t("rec_saved"));
      nav(`/journal/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setSaving(false);
    }
  };
  const statusLabel = { idle: t("rec_ready"), recording: t("rec_recording"), paused: t("rec_paused"), done: t("rec_done") }[r.status];

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20 fade-up">
      <p className="label-gold mb-3">{t("rec_kicker")}</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#F4F6FA] font-semibold mb-10">{t("rec_title")}</h1>

      <div className="glass-card p-6 sm:p-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <span className="label-gold block mb-2">{t("rec_lang")}</span>
            <div className="flex gap-2" data-testid="recorder-lang-select">
              {[["fr", "Français"], ["en", "English"]].map(([v, l]) => (
                <button key={v} data-testid={`recorder-lang-${v}`} disabled={saving} onClick={() => setLang(v)}
                  className={`px-4 py-2 rounded-full text-sm border transition-colors duration-200 ${lang === v ? "bg-[#E5C158] text-[#060A12] border-[#E5C158]" : "border-white/10 text-[#C2C9DB] hover:border-[#E5C158]/50"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-4xl text-[#F4F6FA] tabular-nums" data-testid="recorder-timer">{formatDuration(r.elapsed)}</div>
            <div className={`text-xs uppercase tracking-[0.2em] mt-1 ${r.status === "recording" ? "text-red-400" : "text-[#8E9BB4]"}`} data-testid="recorder-status">
              {r.status === "recording" && <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse mr-2" />}
              {statusLabel}
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-[#060A12]/60 border border-white/5 px-4 py-6">
          <Waveform analyser={r.analyser} active={r.status === "recording"} />
        </div>

        <div className="flex items-center justify-center gap-5 mt-8">
          {r.status === "idle" && <RoundBtn primary testid="recorder-start-btn" onClick={start}><Mic size={28} /></RoundBtn>}
          {r.status === "recording" && (
            <>
              <RoundBtn testid="recorder-pause-btn" onClick={r.pause}><Pause size={20} /></RoundBtn>
              <RoundBtn danger testid="recorder-stop-btn" onClick={r.stop}><Square size={20} /></RoundBtn>
            </>
          )}
          {r.status === "paused" && (
            <>
              <RoundBtn testid="recorder-resume-btn" onClick={r.resume}><Play size={20} /></RoundBtn>
              <RoundBtn danger testid="recorder-stop-btn" onClick={r.stop}><Square size={20} /></RoundBtn>
            </>
          )}
        </div>
        {r.status === "idle" && favorite !== "normal" && (
          <p className="text-center text-xs text-[#8E9BB4] mt-4" data-testid="recorder-default-effect">
            {t("fx_default_is")} <span className="text-[#E5C158]">{t(`fx_${favorite}`)}</span>
          </p>
        )}

        {r.status === "done" && r.blob && (
          <div className="mt-8 space-y-5 fade-up">
            <VoiceEffectPicker value={effect} onChange={setEffect} processing={processing} disabled={saving} favorite={favorite} onSetFavorite={setFavorite} />
            {previewUrl && <audio controls src={previewUrl} className="w-full" data-testid="recorder-preview-audio" />}
            <input data-testid="recorder-title-input" className="input-navy" placeholder={t("rec_title_ph")} value={title} onChange={(e) => setTitle(e.target.value)} />
            <MoodTagEditor mood={mood} tags={tags} onMood={setMood} onTags={setTags} disabled={saving} />
            <div className="flex flex-col sm:flex-row gap-3">
              <button data-testid="recorder-discard-btn" disabled={saving} onClick={r.reset} className="px-5 py-3 rounded-full border border-white/10 text-[#C2C9DB] hover:border-[#E5C158]/40 flex items-center justify-center gap-2">
                <RotateCcw size={16} /> {t("rec_discard")}
              </button>
              <button data-testid="recorder-save-btn" disabled={saving || processing || !fxBlob} onClick={save} className="btn-gold flex-1 px-5 py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-70">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {saving ? t("rec_saving") : t("rec_save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
